import { useState, useEffect, useMemo, useRef } from 'react';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';

import type { ChatConfig } from '@dev-streetbeat/chat';

import { auth } from '../config/firebase';
import JwtAuthService from '../services/JwtAuthService';
import FirebaseTokenService from '../services/FirebaseTokenService';
import assistantNameStore from '../store/assistantNameStore';
import { createLogger } from 'shared/logger';
import { isGatewayResumeEnabled } from '../config/appConfig';

const log = createLogger('ChatConfig');

interface UseChatConfigResult {
  chatConfig: ChatConfig;
  loading: boolean;
  error: string | null;
}

export const useChatConfig = (): UseChatConfigResult => {
  const [firebaseKey, setFirebaseKey] = useState<string | null>(null);
  const [jwtSession, setJwtSession] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const retryCount = useRef<number>(0);

  // Get authentication token
  useEffect(() => {
    const getAuthToken = async () => {
      try {
        if (JwtAuthService.shouldUseJwtAuth()) {
          // For JWT auth, get JWT session ID
          const session = JwtAuthService.getToken();
          setJwtSession(session);
          setFirebaseKey(null);
          setLoading(false);
        } else {
          await auth.authStateReady();
          const token = await auth.currentUser?.getIdToken();
          setFirebaseKey(token || null);
          setJwtSession(null);
          setLoading(false);
        }
      } catch (err) {
        log.error('failed to get auth token', { error: err });
        setError('Authentication failed');
        setLoading(false);
      }
    };

    getAuthToken();
  }, []);

  const baseUrl = window.__APP_CONFIG__?.API_BASE_URL;
  const tenantId = window.__APP_CONFIG__?.TENANT_ID;
  const assistantNameConfig = window.__APP_CONFIG__?.ASSISTANT_NAME;
  const transport = window.__APP_CONFIG__?.TRANSPORT;
  const gatewayResume = isGatewayResumeEnabled();

  const { selectedAssistant, setAvailableAssistants, isInitialized } = assistantNameStore();

  useEffect(() => {
    if (assistantNameConfig) {
      const assistants = assistantNameConfig.split(',').map((name) => name.trim()).filter(Boolean);
      setAvailableAssistants(assistants);
    }
  }, [assistantNameConfig, setAvailableAssistants]);

  const assistantName = isInitialized && selectedAssistant 
    ? selectedAssistant 
    : assistantNameConfig?.split(',')[0]?.trim() || assistantNameConfig;

  // Create chat config with appropriate headers
  const chatConfig: ChatConfig = useMemo(() => {
    const headers: Record<string, string | undefined> = {
      AppVersionEpoch: '99999999999',
      'X-Tenant-Id': tenantId,
    };

    // Add appropriate auth header
    if (jwtSession) {
      headers['Jwt-Jwks'] = jwtSession;
    } else if (firebaseKey) {
      headers['Firebase'] = firebaseKey;
    }

    return {
      baseUrl,
      requestHeadersMap: headers,
      axiosInterceptors: {
        request: {
          onFulfilled: async (config) => {
            // Check and refresh token before each request
            if (!JwtAuthService.shouldUseJwtAuth()) {
              const validToken = await FirebaseTokenService.getValidToken(firebaseKey);
              if (validToken) {
                config.headers.Firebase = validToken;

                // Update state if token was refreshed
                if (validToken !== firebaseKey) {
                  setFirebaseKey(validToken);
                }
              }
            }
            return config;
          },
        },
        response: {
          onRejected: async (error: AxiosError & { config: AxiosRequestConfig }) => {
            if (error.response?.status === 401 && error.config) {
              if (retryCount.current >= 3) {
                log.error('401 retry limit reached, authentication failed', { attempts: retryCount.current });
                return Promise.reject(error);
              }
              retryCount.current++;
              try {
                // refresh firebase token
                const token = await FirebaseTokenService.refreshToken();
                if (token) {
                  // Update the stored token
                  setFirebaseKey(token);

                  // Update the failed request headers and retry
                  error.config.headers.Firebase = token;

                  // Retry the request with the new token
                  const response = await axios.request(error.config);

                  retryCount.current = 0;

                  return response;
                }
              } catch (refreshError) {
                log.warn('Firebase token refresh failed on 401 retry', {
                  attempt: retryCount.current,
                  error: refreshError,
                });

                // If this was the last retry attempt, reject with the refresh error
                if (retryCount.current >= 3) {
                  log.error('token refresh retry limit reached, authentication failed', {
                    attempts: retryCount.current,
                  });
                  return Promise.reject(refreshError);
                }

                // Otherwise, continue to retry
                return Promise.reject(error);
              }
            }
            return Promise.reject(error);
          },
        },
      },
      debugMode: process.env.NODE_ENV === 'development',
      assistantName,
      transport: transport || 'sse',
      // a spread: the chat SDK in package.json (16.4.x) predates the option and ignores it
      ...(gatewayResume ? { gatewayResume: true } : {}),
    };
  }, [baseUrl, firebaseKey, jwtSession, tenantId, assistantName, transport, gatewayResume]);

  return { chatConfig, loading, error };
};
